import { Component } from "react";
import i18n from "@/i18n";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
} from "@homy/ui";
import { AlertCircle } from "lucide-react";

function isDevEnvironment() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      return true;
    }
  } catch {
    // Catch ReferenceError if process/import.meta is undefined or stripped in test runtimes
  }
  try {
    if (typeof process !== "undefined" && process?.env?.NODE_ENV === "development") {
      return true;
    }
  } catch {
    // In standard browser runtimes process is not defined
  }
  return false;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("Error caught by boundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback({
            error: this.state.error,
            resetErrorBoundary: this.reset,
          });
        }
        return this.props.fallback;
      }

      const title = i18n.t("common:errorBoundary.title");
      const message = i18n.t("common:errorBoundary.message");
      const refreshLabel = i18n.t("common:errorBoundary.refresh");
      const goHomeLabel = i18n.t("common:errorBoundary.goHome");

      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-lg p-6 border-border shadow-lg">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-foreground">{title}</AlertTitle>
              <AlertDescription className="text-muted-foreground mt-1">
                {message}
              </AlertDescription>
            </Alert>

            {isDevEnvironment() && this.state.error && (
              <div className="mb-6 p-4 rounded-xl bg-muted text-muted-foreground text-xs overflow-auto max-h-40">
                <pre className="whitespace-pre-wrap break-words">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
                className="min-h-[44px] flex-1 sm:flex-none"
              >
                {refreshLabel}
              </Button>
              <Button
                type="button"
                onClick={this.handleReset}
                className="min-h-[44px] flex-1 sm:flex-none"
              >
                {goHomeLabel}
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
